import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { ReactNode, Suspense } from 'react';
import { IconButton } from './IconButton';
import classNames from 'classnames';
import { LinearLoader } from './LinearLoader';
import { FullLoader } from './Loader';
import { Button } from './Button';
import { LinkProps } from '@tanstack/react-router';

export type ModalIcon =
  | 'critical'
  | 'info'
  | 'success-info'
  | 'success'
  | 'warning';

interface ModalProps {
  open: boolean;
  icon?: ModalIcon | undefined;
  onClose?: (() => void) | undefined;
  dialogPanelClassname?: string | undefined;
  title?: ReactNode | undefined;
  children: ReactNode;
  hideTopHr?: boolean | undefined;
  scroll?: boolean | undefined;
  loading?: boolean | undefined;
  veryDarkDialogBackdrop?: boolean | undefined;
}

export const ModalFooter = ({
  onSubmit,
  closeText = 'Cancel',
  hideCloseText,
  componentLeft,
  isDelete,
  submitText,
  hideBottomHr,
  buttonsDisabled,
  onClose,
  asForm,
  linkProps,
}: {
  isDelete?: boolean | undefined;
  onSubmit?: (() => void) | undefined;
  onClose: (() => void);
  hideCloseText?: boolean | undefined;
  componentLeft?: ReactNode | undefined;
  hideBottomHr?: boolean | undefined;
  buttonsDisabled?: boolean | undefined;
  submitText?: string | undefined;
  closeText?: string | undefined;
  asForm?: boolean | undefined;
  linkProps?: LinkProps | undefined;
}) => (
  <>
    {!hideBottomHr && (
      <hr className="border-none shrink-0 h-px w-full bg-grey-700" />
    )}
    <div className="flex items-center p-5 h-21 justify-between">
      {componentLeft && (
        <div className="flex items-center justify-start">{componentLeft}</div>
      )}
      <div className="flex items-center justify-end grow space-x-3">
        {!hideCloseText && (
          <Button
            button={{
              text: closeText,
              shrink: true,
              type: isDelete ? 'secondary' : 'tertiary',
              onClick: () => onClose(),
              disabled: buttonsDisabled,
            }}
          />
        )}
        {submitText && (
          <Button
            button={{
              text: submitText,
              type: isDelete ? 'delete' : 'primary',
              isSubmit: asForm,
              shrink: true,
              disabled: buttonsDisabled,
              onClick: asForm
                ? undefined
                : (e) => {
                    if (onSubmit) {
                      e.preventDefault();

                      onSubmit();
                    } else {
                      onClose();
                    }
                  },
            }}
            link={linkProps}
          />
        )}
      </div>
    </div>
  </>
);

export function Modal({
  open,
  onClose,
  title,
  children,
  icon,
  hideTopHr,
  scroll = true,
  loading,
  dialogPanelClassname,
  veryDarkDialogBackdrop,
}: ModalProps) {
  return (
    <Dialog
      transition
      className={`fixed inset-0 z-100 font-open`}
      open={open}
      onClose={() => onClose?.()}
    >
      <DialogBackdrop
        transition
        className={classNames('fixed inset-0 bg-black/50 transition duration-300 ease-out backdrop-blur-md data-closed:backdrop-blur-none data-closed:bg-transparent data-closed:ease-in', {
          'bg-black/70': veryDarkDialogBackdrop,
        })}
      />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel
          transition
          className={`flex flex-col shadow-lg overflow-hidden max-h-[80%] text-white rounded transition duration-200 ease-out data-closed:duration-150 data-closed:ease-in data-closed:translate-y-4 data-closed:opacity-0 border  border-grey-t-700 bg-background-tertiary ${dialogPanelClassname}`}
        >
          <>
              {loading && <LinearLoader />}
              <div className="flex items-center justify-between p-5 relative">
                <div className="flex items-center space-x-5 mr-12">
                  {(!!icon || !!title) && (
                    <>
                      {icon === 'warning' && (
                        <div className="p-3 bg-amber-t-600/10 rounded-sm">
                          <ExclamationTriangleIcon className="size-6 text-amber-300" />
                        </div>
                      )}
                      {icon === 'critical' && (
                        <div className="p-3 bg-red-t-600/10 rounded-sm">
                          <ExclamationCircleIcon className="size-6 text-red-300" />
                        </div>
                      )}
                      {icon === 'success' && (
                        <div className="p-3 bg-primary-t-600/10 rounded-sm">
                          <CheckCircleIcon className="size-6 text-primary-300" />
                        </div>
                      )}
                      {icon === 'info' && (
                        <div className="p-3 bg-secondary-t-600/10 rounded-sm">
                          <BoltIcon className="size-6 text-secondary-300" />
                        </div>
                      )}
                      {icon === 'success-info' && (
                        <div className="p-3 bg-primary-t-600/10 rounded-sm">
                          <BoltIcon className="size-6 text-primary-300" />
                        </div>
                      )}
                      {!!title &&
                        (typeof title === 'string' ? (
                          <h2 className="font-grotesque text-h2 font-bold">
                            {title}
                          </h2>
                        ) : (
                          title
                        ))}
                    </>
                  )}
                </div>
                {onClose && (
                  <div
                    className={classNames({
                      'absolute right-1 top-2 z-50': !!icon,
                    })}
                  >
                    <IconButton Icon={XMarkIcon} onClick={() => onClose()} />
                  </div>
                )}
              </div>
              {!icon && !hideTopHr && (
                <hr className="border-none shrink-0 h-px w-full bg-grey-700" />
              )}
              <Suspense fallback={<FullLoader className="size-6" />}>
                <div
                  className={classNames(
                    'flex flex-col grow',
                    scroll
                      ? 'overflow-y-auto overflow-x-hidden'
                      : 'overflow-hidden'
                  )}
                >
                  {children}
                </div>
              </Suspense>
          </>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
